import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTabsStore } from '@/stores/tabsStore'
import { useTabLimitStore } from '@/stores/tabLimitStore'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import type { ProjectTab } from '@/types'

const DIALECT_BADGE: Record<string, string> = {
  postgresql: 'PG',
  mysql: 'MySQL',
  mariadb: 'Maria',
  sqlserver: 'MSSQL',
  oracle: 'Oracle',
  sqlite: 'SQLite',
}

export function TabLimitDialog() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const open = useTabLimitStore((s) => s.open)
  const pendingUrl = useTabLimitStore((s) => s.pendingUrl)
  const hide = useTabLimitStore((s) => s.hide)

  const tabs = useTabsStore((s) => s.tabs)
  const closeTab = useTabsStore((s) => s.closeTab)

  function handleCloseTab(tab: ProjectTab) {
    closeTab(tab.id)
    hide()
    if (pendingUrl) navigate(pendingUrl)
  }

  return (
    <Dialog
      open={open}
      title={t('tabs.limitDialog.title', 'Limite de abas atingido')}
      onClose={hide}
      size="md"
      footer={
        <Button variant="ghost" onClick={hide}>
          {t('common.cancel')}
        </Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.5 }}>
          {t(
            'tabs.limitDialog.description',
            'Você já tem {count} projetos abertos. Feche um para abrir o novo.',
            { count: tabs.length },
          )}
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            maxHeight: 340,
            overflowY: 'auto',
          }}
        >
          {tabs.map((tab) => {
            const badge = DIALECT_BADGE[tab.dialect] ?? tab.dialect
            const name = tab.remoteName ?? tab.title

            return (
              <div
                key={tab.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--bg-card)',
                }}
              >
                {/* Dirty indicator */}
                {tab.isDirty && (
                  <span
                    title={t('tabs.unsaved', 'Alterações não salvas')}
                    style={{ fontSize: 13, flexShrink: 0 }}
                    aria-label={t('tabs.unsaved', 'Alterações não salvas')}
                  >
                    ⚠
                  </span>
                )}

                {/* Dialect badge */}
                <span
                  style={{
                    flexShrink: 0,
                    padding: '1px 5px',
                    borderRadius: 4,
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    background: 'var(--bg-elevated, #2a2a2a)',
                    color: 'var(--text-dim)',
                    border: '1px solid var(--border)',
                  }}
                >
                  {badge}
                </span>

                {/* Name */}
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={name}
                >
                  {name}
                </span>

                {/* Close button */}
                <button
                  type="button"
                  onClick={() => handleCloseTab(tab)}
                  style={{
                    flexShrink: 0,
                    padding: '4px 12px',
                    borderRadius: 6,
                    border: tab.isDirty
                      ? '1px solid var(--color-danger-border)'
                      : '1px solid var(--border)',
                    background: 'transparent',
                    color: tab.isDirty ? 'var(--color-danger)' : 'var(--text)',
                    fontSize: 12,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--bg-hover)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent'
                  }}
                >
                  {tab.isDirty
                    ? t('tabs.limitDialog.closeUnsaved', 'Fechar sem salvar')
                    : t('tabs.limitDialog.close', 'Fechar')}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </Dialog>
  )
}
